"use client";

import { useState } from "react";
import { Icon } from "@iconify/react";
import { uiIcons } from "@/config/icons";
import { PDFDocument } from "pdf-lib";
import { FileUploader } from "@/components/ui/FileUploader";
import { usePathname } from "next/navigation";
import { getLocaleFromPathname } from "@/lib/i18n";
import { getPdfToolCopy } from "@/lib/pdf-tool-copy";
import { getToolUiCopy } from "@/lib/tool-ui-copy";

export function SplitPdf() {
  const [file, setFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [splitOption, setSplitOption] = useState<"all" | "range">("all");
  const [startPage, setStartPage] = useState(1);
  const [endPage, setEndPage] = useState(1);
  const pathname = usePathname() || "/";
  const locale = getLocaleFromPathname(pathname);
  const copy = getPdfToolCopy(locale);
  const ui = getToolUiCopy(locale);

  const handleFilesSelected = async (files: File[]) => {
    if (files.length > 0) {
      const selectedFile = files[0];
      setFile(selectedFile);

      try {
        const fileBytes = await selectedFile.arrayBuffer();
        const pdf = await PDFDocument.load(fileBytes);
        const count = pdf.getPageCount();
        setPageCount(count);
        setEndPage(count);
      } catch (error) {
        console.error("Error loading PDF:", error);
        alert(copy.errorLoadingPdfFile);
      }
    }
  };

  const splitPDF = async () => {
    if (!file) return;

    setIsProcessing(true);

    try {
      const fileBytes = await file.arrayBuffer();
      const pdfDoc = await PDFDocument.load(fileBytes);

      if (splitOption === "all") {
        // Split into individual pages
        for (let i = 0; i < pageCount; i++) {
          const newPdf = await PDFDocument.create();
          const [copiedPage] = await newPdf.copyPages(pdfDoc, [i]);
          newPdf.addPage(copiedPage);

          const pdfBytes = await newPdf.save();
          const blob = new Blob([pdfBytes as BlobPart], { type: "application/pdf" });
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `page-${i + 1}.pdf`;
          link.click();
          URL.revokeObjectURL(url);
        }
      } else {
        // Split range
        const newPdf = await PDFDocument.create();
        const pages = [];
        for (let i = startPage - 1; i < endPage; i++) {
          pages.push(i);
        }
        const copiedPages = await newPdf.copyPages(pdfDoc, pages);
        copiedPages.forEach((page) => newPdf.addPage(page));

        const pdfBytes = await newPdf.save();
        const blob = new Blob([pdfBytes as BlobPart], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `pages-${startPage}-${endPage}.pdf`;
        link.click();
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error("Error splitting PDF:", error);
      alert(copy.errorSplittingPdf);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setPageCount(0);
    setSplitOption("all");
    setStartPage(1);
    setEndPage(1);
  };

  return (
    <div className="space-y-8">
      {!file ? (
        <div className="space-y-4">
           <FileUploader 
             accept=".pdf" 
             onFilesSelected={handleFilesSelected}
           />
        </div>
      ) : (
        <div className="space-y-6">
          <div className="rounded-[30px] border-2 border-positivus-dark bg-white p-6 md:p-8">
            <div className="flex items-center gap-6">
              <div className="flex h-16 w-16 items-center justify-center rounded-[20px] bg-red-100 text-red-600 border border-red-200">
                <Icon icon={uiIcons.fileText} className="h-8 w-8" />
              </div>
              <div>
                <p className="font-bold text-xl text-positivus-dark">{file.name}</p>
                <div className="flex items-center gap-2 mt-1">
                   <p className="text-gray-500 font-medium">{pageCount} {copy.pages}</p>
                </div>
              </div>
              <button 
                onClick={handleReset}
                className="ml-auto text-gray-400 hover:text-red-500 p-2"
                title={ui.remove}
              >
                <Icon icon={uiIcons.refresh} className="h-6 w-6" />
              </button>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="mb-3 block text-lg font-medium text-positivus-dark">{copy.splitOption}</label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => setSplitOption("all")}
                  className={`rounded-[20px] border-2 p-6 text-sm font-medium transition-all ${
                    splitOption === "all"
                      ? "border-positivus-dark bg-positivus-dark text-white shadow-[0_5px_0_0_#191A23]"
                      : "border-positivus-dark/20 hover:border-positivus-dark bg-white hover:bg-positivus-green/10"
                  }`}
                >
                  <div className="mb-2 text-2xl">📄</div>
                  {copy.splitIntoIndividualPages}
                </button>
                <button
                  onClick={() => setSplitOption("range")}
                  className={`rounded-[20px] border-2 p-6 text-sm font-medium transition-all ${
                    splitOption === "range"
                      ? "border-positivus-dark bg-positivus-dark text-white shadow-[0_5px_0_0_#191A23]"
                      : "border-positivus-dark/20 hover:border-positivus-dark bg-white hover:bg-positivus-green/10"
                  }`}
                >
                  <div className="mb-2 text-2xl">📋</div>
                  {copy.extractPageRange}
                </button>
              </div>
            </div>

            {splitOption === "range" && (
              <div className="grid grid-cols-2 gap-4 p-6 rounded-[20px] bg-positivus-green/10 border border-positivus-dark/10">
                <div>
                  <label className="mb-2 block text-sm font-medium text-positivus-dark">{copy.startPage}</label>
                  <input
                    type="number"
                    min="1"
                    max={pageCount}
                    value={startPage}
                    onChange={(e) => setStartPage(Math.min(parseInt(e.target.value) || 1, endPage))}
                    className="w-full rounded-[15px] border-2 border-positivus-dark/20 bg-white px-4 py-3 focus:outline-none focus:border-positivus-dark transition-all"
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-positivus-dark">{copy.endPage}</label>
                  <input
                    type="number"
                    min="1"
                    max={pageCount}
                    value={endPage}
                    onChange={(e) => setEndPage(Math.max(parseInt(e.target.value) || pageCount, startPage))}
                    className="w-full rounded-[15px] border-2 border-positivus-dark/20 bg-white px-4 py-3 focus:outline-none focus:border-positivus-dark transition-all"
                  />
                </div>
              </div>
            )}
          </div>

          <button
            onClick={splitPDF}
            disabled={isProcessing}
            className="w-full rounded-[20px] bg-positivus-dark px-6 py-4 text-xl font-medium text-white hover:bg-positivus-green hover:text-positivus-dark transition-all shadow-[0_5px_0_0_#191A23] hover:shadow-none hover:translate-y-[2px] disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none disabled:hover:translate-y-0 disabled:hover:bg-positivus-dark disabled:hover:text-white"
          >
            {isProcessing ? (
                <div className="flex items-center justify-center gap-3">
                    <Icon icon="mdi:loading" className="h-6 w-6 animate-spin" />
                    <span>{copy.processingPdf}</span>
                </div>
            ) : splitOption === "all" ? copy.splitIntoIndividualPages : `${copy.extractPageRange} ${startPage}-${endPage}`}
          </button>
        </div>
      )}
    </div>
  );
}
